import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem, MessageService, SortEvent } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { RucBuscaComponent } from '../../shared/components/ruc-busca/ruc-busca.component';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import { PessoaRucResultado } from '../../models/pessoas/pessoa-ruc-resultado.model';
import { PessoaService } from '../../services/pessoas/pessoas.service';
import { CidadeService } from '../../services/configuracoes/cidade.service';
import { PessoaDTO, PessoaFormDTO } from '../../models/clientes/pessoa.model';
import { TipoPessoa } from '../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../enums/sexo.enum';
import { TableColumn } from '../../models/shared/table-column.model';
import { PaisDDI } from '../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../shared/data/paises-ddi.data';
import { filtrarRucDoEvento, isDvRucValido } from '../../shared/utils/ruc.util';

@Component({
  selector: 'app-clientes',
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class ClientesComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO: quando true, a tela é reusada dentro de outra (ex.: aba
   * "Gestão de clientes" do Operar Cliente, sob o contexto do cliente). Esconde
   * o cabeçalho da página e as ações que saem do contexto (excluir + menu extra).
   */
  @Input() embutido = false;

  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  // Estado do formulario
  form: PessoaFormDTO = this.createEmptyForm();
  cidadeList: { label: string; value: number; estado: string }[] = [];
  estadoCidade: string = '';
  tipoPessoaOptions: { value: TipoPessoa; label: string }[] = [];
  tipoNaturezaFiscalOptions: { value: TipoNaturezaFiscal; label: string }[] = [];
  sexoOptions: { value: Sexo; label: string }[] = [];
  isFormSubmitted: boolean = false;

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  // Dados da tabela
  pessoas: any[] = [];
  pessoasOriginal: any[] = [];
  first3: number = 0;
  rows3: number = 10;
  /** Guarda o ultimo valor de "rows3" processado pelo onRowsPerPageChange,
   *  para evitar chamadas duplicadas de findClientes quando o (ngModelChange)
   *  do dropdown de itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  private searchSubject$ = new Subject<string>();
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Busca automatica por CI
  private ciInput$ = new Subject<string>();
  private ciDestroy$ = new Subject<void>();

  // Dialog pessoa ja cadastrada
  pessoaJaCadastradaDialogVisible = false;
  pessoaJaCadastradaMensagem = '';
  pessoaJaCadastradaId: number | null = null;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'clientes-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoPessoa', labelKey: 'clientes.labels.codigo_cliente', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'clientes.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'fantasia', labelKey: 'clientes.labels.nome_fantasia', visible: true, fixed: false, defaultVisible: true },
    { key: 'telefone', labelKey: 'clientes.labels.telefone', visible: true, fixed: false, defaultVisible: true },
    { key: 'ci', labelKey: 'clientes.labels.ci', visible: true, fixed: false, defaultVisible: true },
    { key: 'ruc', labelKey: 'clientes.labels.ruc', visible: true, fixed: false, defaultVisible: true },
    { key: 'cidade', labelKey: 'clientes.labels.cidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'clientes.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'clientes.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    titulo_criar: '',
    subtitulo_criar: '',
    titulo_editar: '',
    subtitulo_editar: '',
    identificacao: '',
    contato: '',
    dados_pessoais: '',
    endereco: '',
    lista_de_clientes: '',
    nenhum_cliente: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: '',
    cliente_criado_sucesso: '',
    cliente_atualizado_sucesso: '',
    erro_ao_criar_cliente: '',
    erro_ao_atualizar_cliente: '',
    cliente_excluido_sucesso: '',
    erro_ao_excluir_cliente: '',
    data_nascimento_invalida: '',
    aba_cadastro: '',
    aba_lista: ''
  };

  labels = {
    ruc: '', ci: '', nome: '', nome_fantasia: '',
    tipo_pessoa: '', tipo_natureza_fiscal: '',
    categoria: '', cliente: '', provedor: '',
    ddi: '', telefone: '', email: '',
    data_nascimento: '', sexo: '',
    cidade: '', estado: '', endereco: '', numero: '',
    codigo_cliente: '', data_inclusao: '', acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    ruc: '', ci: '', nome: '', nome_fantasia: '',
    tipo_pessoa: '', tipo_natureza_fiscal: '',
    ddi: '', telefone: '', email: '',
    sexo: '', cidade: '',
    endereco: '', numero: '',
    pesquisar: ''
  };

  botoes = {
    salvar: '', atualizar: '', limpar: '',
    novo_cliente: '',
    confirmar: '', cancelar: ''
  };

  tooltips = {
    visualizar: '', editar: '', excluir: '',
    adicionar_despesa: '', adicionar_receita: '',
    ver_pedidos: '', ver_titulos_abertos: '', mais_acoes: ''
  };

  // Menu de acoes rapidas
  @ViewChild('menuAcoes') menuAcoes!: Menu;
  @ViewChild('menuAcoesMobile') menuAcoesMobile!: Menu;
  @ViewChild('rucBuscaRef') rucBuscaRef?: RucBuscaComponent;
  menuItems: MenuItem[] = [];
  menuItemsMobile: MenuItem[] = [];

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.findCidades();
    this.findClientes(0, this.rows3);
    this.loadTranslations();
    this.buildTipoPessoaOptions();
    this.buildTipoNaturezaFiscalOptions();
    this.buildSexoOptions();
    this.setDefaultDropdownValues();

    // Verificar query params para comportamento inicial
    this.route.queryParams.subscribe(params => {
      const editId = params['edit'];
      const aba = params['aba'];
      if (editId) {
        this.editarCliente(Number(editId));
      } else if (aba === 'cadastro') {
        this.activeTabIndex = 0;
      }
    });

    // Inicializar debounce de busca por CI
    this.inicializarDebounceCI();

    // Debounce da busca de clientes (400ms)
    this.searchSubject$.pipe(
      debounceTime(400),
      takeUntil(this.ciDestroy$)
    ).subscribe(term => {
      this.buscarClientesNoBackend(term);
    });

    // Monitorar mudanca de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildTipoPessoaOptions();
      this.buildTipoNaturezaFiscalOptions();
      this.buildSexoOptions();
      this.setDefaultDropdownValues();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.ciDestroy$.next();
    this.ciDestroy$.complete();
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('clientes.texts.titulo_criar');
      this.texts.subtitulo = this.translate.instant('clientes.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'clientes.texts.titulo_editar' : 'clientes.texts.titulo_criar'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'clientes.texts.subtitulo_editar' : 'clientes.texts.subtitulo_criar'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'clientes.texts.titulo_criar' : isEdicao ? 'clientes.texts.titulo_editar' : 'clientes.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'clientes.texts.subtitulo_lista'
          : isEdicao ? 'clientes.texts.subtitulo_editar' : 'clientes.texts.subtitulo_criar'
      ),
      titulo_criar: this.translate.instant('clientes.texts.titulo_criar'),
      subtitulo_criar: this.translate.instant('clientes.texts.subtitulo_criar'),
      titulo_editar: this.translate.instant('clientes.texts.titulo_editar'),
      subtitulo_editar: this.translate.instant('clientes.texts.subtitulo_editar'),
      identificacao: this.translate.instant('clientes.texts.identificacao'),
      contato: this.translate.instant('clientes.texts.contato'),
      dados_pessoais: this.translate.instant('clientes.texts.dados_pessoais'),
      endereco: this.translate.instant('clientes.texts.endereco'),
      lista_de_clientes: this.translate.instant('clientes.texts.lista_de_clientes'),
      nenhum_cliente: this.translate.instant('clientes.texts.nenhum_cliente'),
      ajuste_filtros: this.translate.instant('clientes.texts.ajuste_filtros'),
      carregando: this.translate.instant('clientes.texts.carregando'),
      confirmar_exclusao: this.translate.instant('clientes.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('clientes.texts.confirmacao'),
      cliente_criado_sucesso: this.translate.instant('clientes.texts.cliente_criado_sucesso'),
      cliente_atualizado_sucesso: this.translate.instant('clientes.texts.cliente_atualizado_sucesso'),
      erro_ao_criar_cliente: this.translate.instant('clientes.texts.erro_ao_criar_cliente'),
      erro_ao_atualizar_cliente: this.translate.instant('clientes.texts.erro_ao_atualizar_cliente'),
      cliente_excluido_sucesso: this.translate.instant('clientes.texts.cliente_excluido_sucesso'),
      erro_ao_excluir_cliente: this.translate.instant('clientes.texts.erro_ao_excluir_cliente'),
      data_nascimento_invalida: this.translate.instant('clientes.texts.data_nascimento_invalida'),
      aba_cadastro: this.translate.instant('clientes.texts.aba_cadastro'),
      aba_lista: this.translate.instant('clientes.texts.aba_lista')
    };

    this.labels = {
      ruc: this.translate.instant('clientes.labels.ruc'),
      ci: this.translate.instant('clientes.labels.ci'),
      nome: this.translate.instant('clientes.labels.nome'),
      nome_fantasia: this.translate.instant('clientes.labels.nome_fantasia'),
      tipo_pessoa: this.translate.instant('clientes.labels.tipo_pessoa'),
      tipo_natureza_fiscal: this.translate.instant('clientes.labels.tipo_natureza_fiscal'),
      categoria: this.translate.instant('clientes.labels.categoria'),
      cliente: this.translate.instant('clientes.labels.cliente'),
      provedor: this.translate.instant('clientes.labels.provedor'),
      ddi: this.translate.instant('clientes.labels.ddi'),
      telefone: this.translate.instant('clientes.labels.telefone'),
      email: this.translate.instant('clientes.labels.email'),
      data_nascimento: this.translate.instant('clientes.labels.data_nascimento'),
      sexo: this.translate.instant('clientes.labels.sexo'),
      cidade: this.translate.instant('clientes.labels.cidade'),
      estado: this.translate.instant('clientes.labels.estado'),
      endereco: this.translate.instant('clientes.labels.endereco'),
      numero: this.translate.instant('clientes.labels.numero'),
      codigo_cliente: this.translate.instant('clientes.labels.codigo_cliente'),
      data_inclusao: this.translate.instant('clientes.labels.data_inclusao'),
      acoes: this.translate.instant('clientes.labels.acoes'),
      itens_por_pagina: this.translate.instant('clientes.labels.itens_por_pagina')
    };

    this.placeholders = {
      ruc: this.translate.instant('clientes.placeholders.ruc'),
      ci: this.translate.instant('clientes.placeholders.ci'),
      nome: this.translate.instant('clientes.placeholders.nome'),
      nome_fantasia: this.translate.instant('clientes.placeholders.nome_fantasia'),
      tipo_pessoa: this.translate.instant('clientes.placeholders.tipo_pessoa'),
      tipo_natureza_fiscal: this.translate.instant('clientes.placeholders.tipo_natureza_fiscal'),
      ddi: this.translate.instant('clientes.placeholders.ddi'),
      telefone: this.translate.instant('clientes.placeholders.telefone'),
      email: this.translate.instant('clientes.placeholders.email'),
      sexo: this.translate.instant('clientes.placeholders.sexo'),
      cidade: this.translate.instant('clientes.placeholders.cidade'),
      endereco: this.translate.instant('clientes.placeholders.endereco'),
      numero: this.translate.instant('clientes.placeholders.numero'),
      pesquisar: this.translate.instant('clientes.placeholders.pesquisar')
    };

    this.botoes = {
      salvar: this.translate.instant('clientes.botoes.salvar'),
      atualizar: this.translate.instant('clientes.botoes.atualizar'),
      limpar: this.translate.instant('clientes.botoes.limpar'),
      novo_cliente: this.translate.instant('clientes.botoes.novo_cliente'),
      confirmar: this.translate.instant('clientes.botoes.confirmar'),
      cancelar: this.translate.instant('clientes.botoes.cancelar')
    };

    this.tooltips = {
      visualizar: this.translate.instant('clientes.tooltips.visualizar'),
      editar: this.translate.instant('clientes.tooltips.editar'),
      excluir: this.translate.instant('clientes.tooltips.excluir'),
      adicionar_despesa: this.translate.instant('clientes.tooltips.adicionar_despesa'),
      adicionar_receita: this.translate.instant('clientes.tooltips.adicionar_receita'),
      ver_pedidos: this.translate.instant('clientes.tooltips.ver_pedidos'),
      ver_titulos_abertos: this.translate.instant('clientes.tooltips.ver_titulos_abertos'),
      mais_acoes: this.translate.instant('clientes.tooltips.mais_acoes')
    };
  }

  // =========================================================================
  // Opcoes de dropdown
  // =========================================================================

  private buildTipoPessoaOptions(): void {
    this.tipoPessoaOptions = Object.values(TipoPessoa).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`enums.tipo_pessoa.${tipo.toLowerCase()}`)
    }));
  }

  private buildTipoNaturezaFiscalOptions(): void {
    this.tipoNaturezaFiscalOptions = Object.values(TipoNaturezaFiscal).map(natureza => ({
      value: natureza,
      label: this.translate.instant(`enums.tipo_natureza_fiscal.${natureza.toLowerCase()}`)
    }));
  }

  private buildSexoOptions(): void {
    this.sexoOptions = Object.values(Sexo).map(sexo => ({
      value: sexo,
      label: this.translate.instant(`enums.sexo.${sexo.toLowerCase()}`)
    }));
  }

  private setDefaultDropdownValues(): void {
    const tipoPessoaJuridica = this.tipoPessoaOptions.find(o => o.value === TipoPessoa.JURIDICA);
    if (tipoPessoaJuridica && !this.form.tipoPessoa) {
      this.form.tipoPessoa = tipoPessoaJuridica;
    }

    const contribuinte = this.tipoNaturezaFiscalOptions.find(o => o.value === TipoNaturezaFiscal.CONTRIBUINTE);
    if (contribuinte && !this.form.tipoNaturezaFiscal) {
      this.form.tipoNaturezaFiscal = contribuinte;
    }

    const masculino = this.sexoOptions.find(o => o.value === Sexo.MASCULINO);
    if (masculino && !this.form.sexo) {
      this.form.sexo = masculino;
    }
  }

  findCidades(): Promise<void> {
    return new Promise((resolve) => {
      this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
        this.cidadeList = response.content.map((cidade: any) => ({
          label: cidade.nome,
          value: Number(cidade.id),
          estado: cidade.nomeEstado
        }));
        resolve();
      });
    });
  }

  // =========================================================================
  // Formulario
  // =========================================================================

  createEmptyForm(): PessoaFormDTO {
    return {
      nome: '',
      fantasia: undefined,
      ruc: '',
      ci: '',
      email: '',
      ddi: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: true,
      categoriaFornecedor: false,
      categoriaVendedor: false,
      tipoPessoa: undefined,
      tipoNaturezaFiscal: undefined,
      dataNascimento: '',
      sexo: undefined,
    };
  }

  onSelecionarCidade(event: any): void {
    // O showClear do p-dropdown emite onChange com value null: sem cidade, o estado tambem some.
    const valorCidade = event?.value?.value;
    if (valorCidade == null) {
      this.estadoCidade = '';
      return;
    }
    const cidadeSelecionada = this.cidadeList.find(cidade => cidade.value === valorCidade);
    if (cidadeSelecionada) {
      this.estadoCidade = cidadeSelecionada.estado;
    }
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;
    if (valor) {
      const partes = valor.split('/');
      if (partes.length === 3 && partes[2].length > 4) {
        partes[2] = partes[2].substring(0, 4);
        const novaData = partes.join('/');
        input.value = novaData;
        this.form.dataNascimento = novaData;
      }
    }
  }

  validarDataNascimento(): boolean {
    if (!this.form.dataNascimento) {
      return true;
    }
    const partes = this.form.dataNascimento.split('/');
    if (partes.length === 3 && partes[2].length > 4) {
      return false;
    }
    return true;
  }

  onNomeBlur(): void {
    if (this.form.nome && !this.form.fantasia) {
      this.form.fantasia = this.form.nome;
    }
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  isPessoaFisica(): boolean {
    return this.form.tipoPessoa?.value === TipoPessoa.FISICA;
  }

  submit(): void {
    this.isFormSubmitted = true;

    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || (this.isPessoaFisica() && !this.form.sexo) || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.confirmacao,
        detail: this.translate.instant('clientes.texts.campos_obrigatorios'),
      });
      return;
    }

    if (!this.validarCI()) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.confirmacao,
        detail: this.translate.instant('clientes.validacoes.ci_invalido'),
      });
      return;
    }


    if (!this.validarDataNascimento()) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.confirmacao,
        detail: this.texts.data_nascimento_invalida,
      });
      return;
    }

    // Limpar telefone: salvar apenas digitos puros (sem mascara)
    const telefoneLimpo = this.form.telefone?.replace(/\D/g, '') || undefined;

    // dataNascimento já vem no formato dd/MM/yyyy do p-inputMask
    const dataNascimentoFormatada = this.form.dataNascimento || undefined;

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia || undefined,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email || '',
      ddi: this.paisSelecionado.ddi || undefined,
      telefone: telefoneLimpo,
      endereco: this.form.endereco || undefined,
      numero: this.form.numero || undefined,
      cidade_id: this.form.cidade?.value ? Number(this.form.cidade.value) : undefined,
      categoriaCliente: this.form.categoriaCliente,
      categoriaFornecedor: this.form.categoriaFornecedor,
      categoriaVendedor: this.form.categoriaVendedor ?? false,
      tipoPessoa: this.form.tipoPessoa?.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal?.value,
      dataNascimento: dataNascimentoFormatada,
      sexo: this.form.sexo?.value,
    };

    if (this.form.id) {
      // Modo edicao - PUT
      this.pessoaService.put(`/pessoas/${this.form.id}`, body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.texts.confirmacao,
            detail: this.texts.cliente_atualizado_sucesso,
          });
          this.limparFormulario();
          this.findClientes(0, this.rows3, `${this.lastfield},${this.lastSort}`);
          this.activeTabIndex = 1; // Trocar para aba de lista
        },
        error: () => {
          // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
        },
      });
    } else {
      // Modo criacao - POST
      this.pessoaService.post('/pessoas', body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.texts.confirmacao,
            detail: this.texts.cliente_criado_sucesso,
          });
          this.limparFormulario();
          this.findClientes(0, this.rows3, `${this.lastfield},${this.lastSort}`);
          this.activeTabIndex = 1; // Trocar para aba de lista
        },
        error: (err: any) => {
          // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
          // Apenas logar para debugging.
          console.error('Erro ao cadastrar cliente:', err);
        },
      });
    }
  }

  async editarCliente(id: number): Promise<void> {
    // Garantir que cidades estejam carregadas antes de popular o formulario
    if (this.cidadeList.length === 0) {
      await this.findCidades();
    }

    this.pessoaService.getById('/pessoas', String(id)).subscribe((response: any) => {
      const cidadeId = response.cidade?.id;
      const cidadeEncontrada = cidadeId ? this.cidadeList.find(c => c.value === Number(cidadeId)) : undefined;

      this.form = {
        id: response.id,
        nome: response.nome,
        fantasia: response.fantasia,
        ruc: response.ruc,
        ci: response.ci,
        email: response.email,
        ddi: response.ddi,
        telefone: response.telefone,
        endereco: response.endereco,
        numero: response.numero,
        categoriaCliente: Boolean(response.categoriaCliente),
        categoriaFornecedor: Boolean(response.categoriaFornecedor),
        categoriaVendedor: response.categoriaVendedor,
        cidade: cidadeEncontrada,
        tipoPessoa: this.tipoPessoaOptions.find(
          item => item.value === (response.tipoPessoa?.enum || response.tipoPessoa)
        ),
        tipoNaturezaFiscal: this.tipoNaturezaFiscalOptions.find(
          item => item.value === (response.tipoNaturezaFiscal?.enum || response.tipoNaturezaFiscal)
        ),
        dataNascimento: response.dataNascimento || '',
        sexo: this.sexoOptions.find(
          item => item.value === (response.sexo?.enum || response.sexo)
        ),
      };
      this.estadoCidade = response.cidade?.estado?.nome;

      // Converter DDI do backend para PaisDDI
      const ddiComPlus = response.ddi?.startsWith('+') ? response.ddi : `+${response.ddi || '595'}`;
      this.paisSelecionado = PAISES_DDI.find(p => p.ddi === ddiComPlus) || PAISES_DDI[0];

      // Resetar estado de validacao ao carregar dados para edicao
      this.isFormSubmitted = false;

      // Atualizar titulo para modo edicao
      this.texts.titulo = this.translate.instant('clientes.texts.titulo_editar');
      this.texts.subtitulo = this.translate.instant('clientes.texts.subtitulo_editar');

      // Trocar para aba de cadastro com os dados preenchidos
      this.activeTabIndex = 0;

      // Forcar deteccao de mudancas para p-inputMask atualizar
      this.cdr.detectChanges();

      // Scroll para o topo para ver o formulario preenchido
      this.scrollToFormulario();
    });
  }

  limparFormulario(): void {
    this.form = this.createEmptyForm();
    this.estadoCidade = '';
    this.isFormSubmitted = false;
    this.paisSelecionado = PAISES_DDI[0];
    this.rucBuscaRef?.reset();
    this.setDefaultDropdownValues();

    // Voltar titulo para modo criacao
    this.texts.titulo = this.translate.instant('clientes.texts.titulo_criar');
    this.texts.subtitulo = this.translate.instant('clientes.texts.subtitulo_criar');
  }

  novoCliente(): void {
    this.limparFormulario();
    this.activeTabIndex = 0; // Trocar para aba de cadastro
    this.scrollToFormulario();
  }

  scrollToFormulario(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Busca automatica RUC / CI
  // =========================================================================

  /** Handler: RUC encontrado na API externa (pessoa nao cadastrada) — preencher campos */
  onRucPessoaConfirmada(resultado: PessoaRucResultado): void {
    this.form.ruc = resultado.ruc || '';
    if (resultado.nome) this.form.nome = resultado.nome;
    if (resultado.nome && !this.form.fantasia) this.form.fantasia = resultado.nome;
    if (resultado.ddi) {
      const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) this.paisSelecionado = paisEncontrado;
    }
    if (resultado.telefone) this.form.telefone = resultado.telefone;
    if (resultado.cidade?.id) {
      const cidadeEncontrada = this.cidadeList.find(c => c.value === resultado.cidade!.id);
      if (cidadeEncontrada) {
        this.form.cidade = cidadeEncontrada;
        this.estadoCidade = cidadeEncontrada.estado;
      }
    }
  }

  /** Handler: RUC encontrado na API interna (pessoa ja cadastrada) — exibir dialog */
  onRucPessoaVinculada(pessoa: PessoaDTO): void {
    this.pessoaJaCadastradaId = pessoa.id ?? null;
    this.pessoaJaCadastradaMensagem = this.translate.instant('clientes.texts.pessoa_ja_cadastrada', { nome: pessoa.nome });
    this.pessoaJaCadastradaDialogVisible = true;
  }

  /** Handler: campo RUC limpo — resetar campos preenchidos automaticamente */
  onRucLimpo(): void {
    this.form.nome = '';
    this.form.fantasia = undefined;
    this.form.telefone = '';
    this.form.cidade = undefined;
    this.estadoCidade = '';
    this.paisSelecionado = PAISES_DDI[0];
  }

  /**
   * Aviso de DV invalido no RUC (modo edicao). So orienta: o cadastro e
   * salvo mesmo com o DV errado.
   */
  get avisoRucEdicao(): string | null {
    return isDvRucValido(this.form.ruc) === false
      ? this.translate.instant('ruc_busca.erros.dv_invalido')
      : null;
  }

  /** Sanitiza o campo RUC no modo edicao: mesma regra do app (digitos, um hifen, um DV) */
  onRucEdicaoInput(event: Event): void {
    this.form.ruc = filtrarRucDoEvento(event);
  }

  /** Valida CI: se preenchido, deve ter entre 5 e 8 digitos numericos */
  validarCI(): boolean {
    const ci = (this.form.ci || '').trim();
    if (!ci) return true;
    if (!/^\d+$/.test(ci)) return false;
    return ci.length >= 5 && ci.length <= 8;
  }

  /** Sanitiza CI: apenas digitos, max 8 caracteres */
  onCiModelChange(value: string): void {
    const apenasDigitos = (value || '').replace(/\D/g, '').substring(0, 8);
    if (value !== apenasDigitos) {
      this.form.ci = apenasDigitos;
    }
  }

  /** Busca por CI com debounce (apenas cadastro novo) */
  onCiInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Sanitizar: apenas digitos, max 8
    const apenasDigitos = (input.value || '').replace(/\D/g, '').substring(0, 8);
    if (input.value !== apenasDigitos) {
      input.value = apenasDigitos;
      this.form.ci = apenasDigitos;
    }
    if (this.form.id) return;
    const valor = (this.form.ci || '').trim();
    if (!valor || valor.length < 3) return;
    this.ciInput$.next(valor);
  }

  /** Inicializa Subject de debounce para busca por CI */
  private inicializarDebounceCI(): void {
    this.ciInput$.pipe(
      debounceTime(1500),
      takeUntil(this.ciDestroy$),
    ).subscribe(ci => this.buscarPorCI(ci));
  }

  /** Busca pessoa na API interna por CI */
  private buscarPorCI(ci: string): void {
    if (!ci || !this.form.ci || this.form.id) return;
    this.pessoaService.get(`/pessoas/buscar?ci=${ci}&categoriaCliente=true&size=1`).subscribe({
      next: (res: any) => {
        const resultados = res?.content || [];
        if (resultados.length > 0) {
          const pessoa = resultados[0];
          this.pessoaJaCadastradaId = pessoa.id ?? null;
          this.pessoaJaCadastradaMensagem = this.translate.instant('clientes.texts.pessoa_ja_cadastrada', { nome: pessoa.nome });
          this.pessoaJaCadastradaDialogVisible = true;
        }
      },
    });
  }

  /** Navegar para edicao da pessoa ja cadastrada */
  editarPessoaJaCadastrada(): void {
    this.pessoaJaCadastradaDialogVisible = false;
    if (this.pessoaJaCadastradaId) {
      this.editarCliente(this.pessoaJaCadastradaId);
    }
  }

  // =========================================================================
  // Tabela
  // =========================================================================

  findClientes(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc',
  ): void {
    this.loading = true;
    const filter = `categoriaCliente=true`;

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: filter,
      }).subscribe({
        next: (response) => {
          this.pessoasOriginal = response.content || [];
          this.pessoas = [...this.pessoasOriginal];
          this.totalRecords = response.totalElements || this.pessoasOriginal.length;
          this.first3 = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarPessoasLocal(this.searchNome.trim());
          }
        },
        error: (error) => {
          console.error('Erro ao buscar clientes:', error);
          this.pessoasOriginal = [];
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    const term = this.searchNome.trim();
    if (term.length >= 2) {
      this.searchSubject$.next(term);
    } else {
      // Menos de 2 caracteres: recarrega lista completa
      this.first3 = 0;
      this.findClientes(0, this.rows3);
    }
  }

  private buscarClientesNoBackend(term: string): void {
    this.loading = true;
    this.first3 = 0;

    this.pessoaService
      .get('/pessoas/buscar', {
        page: 0,
        size: this.rows3,
        sort: `${this.lastfield},${this.lastSort}`,
        filter: `categoriaCliente=true&buscaGeral=${encodeURIComponent(term)}`,
      }).subscribe({
        next: (response) => {
          this.pessoasOriginal = response.content || [];
          this.pessoas = [...this.pessoasOriginal];
          this.totalRecords = response.totalElements || this.pessoasOriginal.length;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar clientes:', error);
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  /** Filtra pessoas localmente buscando em todas as colunas visiveis */
  private filtrarPessoasLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.pessoas = this.pessoasOriginal.filter(pessoa => {
      if (this.isColumnVisible('codigoPessoa') && pessoa.codigoPessoa != null) {
        if (String(pessoa.codigoPessoa).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && pessoa.nome) {
        if (pessoa.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('fantasia') && pessoa.fantasia) {
        if (pessoa.fantasia.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('telefone') && pessoa.telefone) {
        if (pessoa.telefone.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('ci') && pessoa.ci) {
        if (String(pessoa.ci).toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('ruc') && pessoa.ruc) {
        if (pessoa.ruc.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('cidade') && pessoa.cidade?.nome) {
        if (pessoa.cidade.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && pessoa.criadoEm) {
        const dataFormatada = new Date(pessoa.criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.pessoas.length;
  }

  onPageChange3(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.findClientes(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange(): void {
    // O (ngModelChange) do p-dropdown de "itens por pagina" tambem
    // dispara no bootstrap do PrimeNG com o valor atual, gerando uma chamada
    // duplicada de findClientes alem da feita em ngOnInit. Ignorar quando
    // "rows3" nao mudou de valor.
    if (this.rows3 === this._lastRows) return;
    this._lastRows = this.rows3;
    this.first3 = 0;
    this.findClientes(0, this.rows3, `${this.lastfield},${this.lastSort}`);
  }

  onSortChange(event: any): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evitar chamada duplicada se mesmo campo e mesma direção
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;

    // Ordenar no backend (todos os registros do banco)
    this.first3 = 0;
    const sort = `${field},${order}`;
    const term = this.searchNome?.trim();

    if (term && term.length >= 2) {
      this.loading = true;
      this.pessoaService
        .get('/pessoas/buscar', {
          page: 0,
          size: this.rows3,
          sort,
          filter: `categoriaCliente=true&buscaGeral=${encodeURIComponent(term)}`,
        }).subscribe({
          next: (response) => {
            this.pessoasOriginal = response.content || [];
            this.pessoas = [...this.pessoasOriginal];
            this.totalRecords = response.totalElements || this.pessoasOriginal.length;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          }
        });
    } else {
      this.findClientes(0, this.rows3, sort);
    }
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  deletarCliente(id: string): void {
    // Modo embutido (aba do Operar Cliente, sob contexto do contador): exclusao fora de
    // escopo. Alem de o template ocultar o botao, bloqueamos aqui — a allowlist do contexto
    // nao tem DELETE nenhum, entao a chamada acabaria excluindo na empresa do PROPRIO
    // contador (o interceptor nao injeta o header em DELETE).
    if (this.embutido) {
      return;
    }
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao(): void {
    if (!this.deleteItemId) return;

    this.pessoaService.delete(`/pessoas/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.cliente_excluido_sucesso,
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
        this.findClientes(0, this.rows3, `${this.lastfield},${this.lastSort}`);
      },
      error: () => {
        // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  viewCliente(id: string): void {
    this.router.navigate(['/lista-de-clientes/view', id]);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Acoes rapidas (menu popup)
  // =========================================================================

  toggleMenu(event: Event, pessoa: any): void {
    this.menuItems = this.buildMenuItems(pessoa);
    this.menuAcoes.toggle(event);
  }

  toggleMenuMobile(event: Event, pessoa: any): void {
    this.menuItemsMobile = this.buildMenuItemsMobile(pessoa);
    this.menuAcoesMobile.toggle(event);
  }

  private buildMenuItems(pessoa: any): MenuItem[] {
    return [
      {
        label: this.tooltips.adicionar_despesa,
        icon: 'pi pi-money-bill',
        command: () => this.adicionarDespesa(pessoa.id)
      },
      {
        label: this.tooltips.adicionar_receita,
        icon: 'pi pi-dollar',
        command: () => this.adicionarReceita(pessoa.id)
      },
      {
        label: this.tooltips.ver_pedidos,
        icon: 'pi pi-shopping-cart',
        command: () => this.verPedidos(pessoa.id)
      },
      {
        label: this.tooltips.ver_titulos_abertos,
        icon: 'pi pi-list',
        command: () => this.verTitulosAbertos(pessoa)
      }
    ];
  }

  private buildMenuItemsMobile(pessoa: any): MenuItem[] {
    return [
      {
        label: this.tooltips.editar,
        icon: 'pi pi-pencil',
        command: () => this.editarCliente(pessoa.id)
      },
      {
        label: this.tooltips.excluir,
        icon: 'pi pi-trash',
        styleClass: 'p-menuitem-danger',
        command: () => this.deletarCliente(pessoa.id)
      },
      { separator: true },
      {
        label: this.tooltips.adicionar_despesa,
        icon: 'pi pi-money-bill',
        command: () => this.adicionarDespesa(pessoa.id)
      },
      {
        label: this.tooltips.adicionar_receita,
        icon: 'pi pi-dollar',
        command: () => this.adicionarReceita(pessoa.id)
      },
      {
        label: this.tooltips.ver_pedidos,
        icon: 'pi pi-shopping-cart',
        command: () => this.verPedidos(pessoa.id)
      },
      {
        label: this.tooltips.ver_titulos_abertos,
        icon: 'pi pi-list',
        command: () => this.verTitulosAbertos(pessoa)
      }
    ];
  }

  adicionarDespesa(pessoaId: number): void {
    this.router.navigate(['/nova-despesa'], { queryParams: { pessoaId } });
  }

  adicionarReceita(pessoaId: number): void {
    this.router.navigate(['/nova-receita'], { queryParams: { pessoaId } });
  }

  verPedidos(pessoaId: number): void {
    this.router.navigate(['/vendas-list'], { queryParams: { pessoaId } });
  }

  verTitulosAbertos(pessoa: any): void {
    this.router.navigate(['/contas-a-receber/pessoa', pessoa.id], {
      queryParams: { nome: pessoa.nome || '', origem: 'clientes', tipo: 'ENTRADA' },
    });
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }
}
